import { useEffect, useState } from "react";
import { Link, Route, Routes, useNavigate } from "react-router-dom";
import { Archive, Home as HomeIcon, Moon } from "lucide-react";
import { LogoMark } from "@/components/logo";
import { TopNav } from "@/components/top-nav";
import { buttonVariants } from "@/components/ui/button";
import { BRAND } from "@/lib/brand";
import { cn } from "@/lib/utils";
import { Dashboard } from "./routes/dashboard";
import { Billing } from "./routes/billing";
import { Login } from "./routes/login";
import { Verify } from "./routes/verify";

type Health = { ok: boolean; ts: string };

function Home() {
  const [health, setHealth] = useState<Health | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    void (async () => {
      try {
        const [healthRes, sessionRes] = await Promise.all([
          fetch("/api/health").catch(() => null),
          fetch("/api/session", { credentials: "include" }).catch(() => null),
        ]);

        if (sessionRes?.ok) {
          void navigate("/dashboard", { replace: true });
          return;
        }

        if (healthRes?.ok) {
          const data = (await healthRes.json()) as Health;
          setHealth(data);
        } else {
          setHealth({ ok: false, ts: "" });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  if (loading) {
    return <div className="min-h-screen bg-zinc-50" />;
  }

  const steps = [
    {
      number: "1",
      title: "Sign in to Flock",
      description:
        "Use your email. We'll send a secure sign-in link, so there's no password to create.",
    },
    {
      number: "2",
      title: "Connect ChatGPT",
      description: "Enter a short code to connect the ChatGPT account you already use.",
    },
    {
      number: "3",
      title: "Pair your phone",
      description:
        "Scan one QR code with the ChatGPT app. Your computer is ready to use from anywhere.",
    },
  ];

  const benefits = [
    {
      icon: Moon,
      title: "Works while you're away",
      description:
        "Hand Codex a big task and get on with your day. It keeps going until the job is done — even with your phone in your pocket.",
    },
    {
      icon: Archive,
      title: "Everything stays put",
      description:
        "Files, projects, and settings stay exactly where you left them — tonight, next week, next month.",
    },
    {
      icon: HomeIcon,
      title: "A computer of your own",
      description:
        "Not a shared session that gets wiped when you close the tab. A private machine in the cloud that's always yours.",
    },
  ];

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900">
      <TopNav />

      <main>
        {/* Hero */}
        <section className="mx-auto max-w-3xl px-4 pb-24 pt-24 text-center sm:px-6 sm:pt-32">
          <div className="mx-auto mb-8 inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3.5 py-1.5 text-sm font-medium text-zinc-600 shadow-sm">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            Always on
          </div>

          <h1 className="text-5xl font-semibold tracking-tight sm:text-6xl md:text-7xl">
            Your own Codex.
            <span className="block text-zinc-400">Always on.</span>
          </h1>

          <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-zinc-600">
            Sign in to {BRAND.name}, connect ChatGPT, and pair your phone. In about two minutes,
            Codex has its own always-on computer, ready wherever you are.
          </p>

          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/login"
              className={cn(
                buttonVariants({ size: "lg" }),
                "h-12 bg-zinc-900 px-8 text-base text-zinc-50 hover:bg-zinc-800"
              )}
            >
              Get started
            </Link>
            <a
              href="#how-it-works"
              className={cn(
                buttonVariants({ variant: "outline", size: "lg" }),
                "h-12 border-zinc-300 bg-white px-8 text-base text-zinc-700 hover:bg-zinc-100"
              )}
            >
              See how it works
            </a>
          </div>

          <p className="mt-5 text-sm text-zinc-500">Three steps. About two minutes.</p>
        </section>

        {/* How it works */}
        <section id="how-it-works" className="border-y border-zinc-200 bg-white">
          <div className="mx-auto max-w-5xl px-4 py-20 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
                Up and running in three steps
              </h2>
              <p className="mt-3 text-lg text-zinc-600">
                No installs, no setup guides, nothing technical.
              </p>
            </div>

            <ol className="mt-14 grid gap-10 sm:grid-cols-3">
              {steps.map((step) => (
                <li key={step.number} className="text-center sm:text-left">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-zinc-900 text-lg font-semibold text-zinc-50 sm:mx-0">
                    {step.number}
                  </div>
                  <h3 className="mt-5 text-lg font-semibold tracking-tight">{step.title}</h3>
                  <p className="mt-2 leading-relaxed text-zinc-600">{step.description}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Always on */}
        <section className="mx-auto max-w-5xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              Yours. And it never turns off.
            </h2>
            <p className="mt-3 text-lg text-zinc-600">
              Most AI assistants stop working the moment you look away. Yours doesn't.
            </p>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-3">
            {benefits.map((benefit) => (
              <div
                key={benefit.title}
                className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-zinc-100">
                  <benefit.icon size={20} className="text-zinc-700" />
                </div>
                <h3 className="mt-4 text-base font-semibold tracking-tight">{benefit.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Use cases */}
        <section className="border-y border-zinc-200 bg-white">
          <div className="mx-auto max-w-5xl px-4 py-20 sm:px-6 lg:px-8">
            <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-zinc-500">
                  Codex, remotely
                </p>
                <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
                  A cloud computer built for your agent
                </h2>
                <p className="mt-5 text-lg leading-relaxed text-zinc-600">
                  Flock is an always-on VPS for your Codex agent, without the server setup. Your
                  agent gets a private Codex computer in the cloud where projects and settings
                  persist between tasks.
                </p>
                <p className="mt-4 leading-relaxed text-zinc-600">
                  Instead of learning how to deploy Codex to a VPS, connect your account and pair
                  your phone. Flock handles the remote computer so you can focus on the work.
                </p>
              </div>

              <dl className="grid gap-px overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-200 sm:grid-cols-3 lg:grid-cols-1">
                <div className="bg-zinc-50 p-6">
                  <dt className="font-semibold tracking-tight">Remote from your phone</dt>
                  <dd className="mt-2 text-sm leading-relaxed text-zinc-600">
                    Start and follow Codex work wherever you are, while the computer stays online.
                  </dd>
                </div>
                <div className="bg-zinc-50 p-6">
                  <dt className="font-semibold tracking-tight">No VPS administration</dt>
                  <dd className="mt-2 text-sm leading-relaxed text-zinc-600">
                    Skip provisioning, command-line setup, and keeping a remote server running.
                  </dd>
                </div>
                <div className="bg-zinc-50 p-6">
                  <dt className="font-semibold tracking-tight">A persistent workspace</dt>
                  <dd className="mt-2 text-sm leading-relaxed text-zinc-600">
                    Your cloud agent keeps its projects, files, and configuration between sessions.
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="border-t border-zinc-200 bg-white">
          <div className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6">
            <LogoMark size={40} className="mx-auto text-zinc-900" />
            <h2 className="mt-6 text-3xl font-semibold tracking-tight sm:text-4xl">
              Give ChatGPT a computer.
            </h2>
            <p className="mx-auto mt-3 max-w-md text-lg text-zinc-600">
              Your own always-on computer is a couple of minutes away.
            </p>
            <Link
              to="/login"
              className={cn(
                buttonVariants({ size: "lg" }),
                "mt-8 h-12 bg-zinc-900 px-8 text-base text-zinc-50 hover:bg-zinc-800"
              )}
            >
              Get started
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-zinc-200 bg-zinc-50">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-4 px-4 py-8 sm:flex-row sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-zinc-500">
            <LogoMark size={18} />
            <span className="text-sm">
              © {new Date().getFullYear()} {BRAND.name}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <span
              className={`h-2 w-2 rounded-full ${health?.ok ? "bg-emerald-500" : "bg-red-500"}`}
            />
            {health?.ok ? "All systems go" : "Having a hiccup — back soon"}
          </div>
        </div>
      </footer>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/verify" element={<Verify />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/billing" element={<Billing />} />
    </Routes>
  );
}
